import { expectArray, expectBoolean, expectNullable, expectNumber, expectRecord, expectString } from "./schema";
import { parseSafePaymentCard } from "./tenant-application-payment-setup";
import { STUDENT_BILLING_ASSIGNMENT_STATUSES, STUDENT_BILLING_CHARGE_TYPES, STUDENT_BILLING_COLLECTION_ROUTES, STUDENT_BILLING_PAYER_STATUSES, STUDENT_BILLING_PAYER_TYPES, STUDENT_BILLING_UNIT_STATUSES, parseStudentBillingIdentifier } from "./student-billing";
import { STUDENT_BILLING_PAYMENT_SETUP_STATUSES } from "./student-billing-payment-setup";
import { parseStudentBillingAdminCollectionPreferenceDto } from "./student-billing-collection-authority";
function assertExactKeys(record, allowed, path) {
    const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
    if (unknown.length > 0)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
function parseNullableString(value, path) {
    return expectNullable(value, (item, itemPath) => expectString(item, itemPath), path);
}
function parseEnum(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function parsePositiveInteger(value, path) {
    const parsed = expectNumber(value, path);
    if (!Number.isSafeInteger(parsed) || parsed < 1)
        throw new Error(`${path} must be a positive integer`);
    return parsed;
}
function parseNonNegativeInteger(value, path) {
    const parsed = expectNumber(value, path);
    if (!Number.isSafeInteger(parsed) || parsed < 0)
        throw new Error(`${path} must be a non-negative integer`);
    return parsed;
}
export function parseStudentBillingAdminCapabilities(value, path = "studentBillingAdminCapabilities") {
    const record = expectRecord(value, path);
    const keys = [
        "canReadBilling",
        "canConfigureRegularAmount",
        "canAssignPayer",
        "canCreateOneOff",
        "canAdjustAmount",
        "canLockBillingUnit",
        "canExecutePayment",
        "canConfigureCollectionMethod"
    ];
    assertExactKeys(record, keys, path);
    return Object.fromEntries(keys.map((key) => [key, expectBoolean(record[key], `${path}.${key}`)]));
}
export function parseStudentBillingAdminCommercialAuthority(value, path = "studentBillingAdminCommercialAuthority") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["contractPricingMode", "contractStatus", "serviceRestrictionStatus", "commercialStateVersion"], path);
    return {
        contractPricingMode: parseEnum(record.contractPricingMode, ["paymentProcessing", "management"], `${path}.contractPricingMode`),
        contractStatus: parseEnum(record.contractStatus, ["trialing", "active", "cancellationScheduled", "ended"], `${path}.contractStatus`),
        serviceRestrictionStatus: parseEnum(record.serviceRestrictionStatus, ["none", "paymentRestricted", "riskSuspended", "operatorSuspended"], `${path}.serviceRestrictionStatus`),
        commercialStateVersion: parsePositiveInteger(record.commercialStateVersion, `${path}.commercialStateVersion`)
    };
}
export function parseStudentBillingAdminPaymentMethod(value, path = "studentBillingAdminPaymentMethod") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["accountReady", "paymentMethodReady", "setupStatus", "card", "updatedAt"], path);
    return {
        accountReady: expectBoolean(record.accountReady, `${path}.accountReady`),
        paymentMethodReady: expectBoolean(record.paymentMethodReady, `${path}.paymentMethodReady`),
        setupStatus: parseEnum(record.setupStatus, STUDENT_BILLING_PAYMENT_SETUP_STATUSES, `${path}.setupStatus`),
        card: record.card === null ? null : parseSafePaymentCard(record.card),
        updatedAt: parseNullableString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudentBillingAdminPayer(value, path = "studentBillingAdminPayer") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["payerId", "payerType", "platformUserId", "status", "paymentMethod"], path);
    return {
        payerId: expectString(record.payerId, `${path}.payerId`),
        payerType: parseEnum(record.payerType, STUDENT_BILLING_PAYER_TYPES, `${path}.payerType`),
        platformUserId: parseNullableString(record.platformUserId, `${path}.platformUserId`),
        status: parseEnum(record.status, STUDENT_BILLING_PAYER_STATUSES, `${path}.status`),
        paymentMethod: parseStudentBillingAdminPaymentMethod(record.paymentMethod, `${path}.paymentMethod`)
    };
}
function parseRegularSetting(value, path) {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["settingId", "tenantId", "studentId", "effectiveFrom", "effectiveTo", "amount", "currency", "version", "createdAt", "updatedAt"], path);
    if (record.currency !== "JPY")
        throw new Error(`${path}.currency must be JPY`);
    return {
        settingId: expectString(record.settingId, `${path}.settingId`),
        tenantId: expectString(record.tenantId, `${path}.tenantId`),
        studentId: expectString(record.studentId, `${path}.studentId`),
        effectiveFrom: expectString(record.effectiveFrom, `${path}.effectiveFrom`),
        effectiveTo: parseNullableString(record.effectiveTo, `${path}.effectiveTo`),
        amount: parseNonNegativeInteger(record.amount, `${path}.amount`),
        currency: "JPY",
        version: parsePositiveInteger(record.version, `${path}.version`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
function parsePayerAssignment(value, path) {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["assignmentId", "tenantId", "studentId", "payerId", "effectiveFrom", "effectiveTo", "status", "version", "createdAt", "updatedAt"], path);
    return {
        assignmentId: expectString(record.assignmentId, `${path}.assignmentId`),
        tenantId: expectString(record.tenantId, `${path}.tenantId`),
        studentId: expectString(record.studentId, `${path}.studentId`),
        payerId: expectString(record.payerId, `${path}.payerId`),
        effectiveFrom: expectString(record.effectiveFrom, `${path}.effectiveFrom`),
        effectiveTo: parseNullableString(record.effectiveTo, `${path}.effectiveTo`),
        status: parseEnum(record.status, STUDENT_BILLING_ASSIGNMENT_STATUSES, `${path}.status`),
        version: parsePositiveInteger(record.version, `${path}.version`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
function parseBillingUnit(value, path) {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["billingUnitId", "tenantId", "studentId", "payerId", "payerAssignmentId", "chargeType", "paidEnrollmentId", "serviceMonth", "oneOffSourceType", "oneOffSourceId", "defaultAmount", "currentPayableAmount", "currency", "amountSource", "collectionRoute", "status", "version", "lockedAt", "lockedByPlatformUserId", "createdAt", "updatedAt"], path);
    if (record.currency !== "JPY" || (record.amountSource !== "default" && record.amountSource !== "manual"))
        throw new Error(`${path} contains invalid amount fields`);
    return {
        billingUnitId: expectString(record.billingUnitId, `${path}.billingUnitId`),
        tenantId: expectString(record.tenantId, `${path}.tenantId`),
        studentId: expectString(record.studentId, `${path}.studentId`),
        payerId: expectString(record.payerId, `${path}.payerId`),
        payerAssignmentId: expectString(record.payerAssignmentId, `${path}.payerAssignmentId`),
        chargeType: parseEnum(record.chargeType, STUDENT_BILLING_CHARGE_TYPES, `${path}.chargeType`),
        paidEnrollmentId: parseNullableString(record.paidEnrollmentId, `${path}.paidEnrollmentId`),
        serviceMonth: parseNullableString(record.serviceMonth, `${path}.serviceMonth`),
        oneOffSourceType: parseNullableString(record.oneOffSourceType, `${path}.oneOffSourceType`),
        oneOffSourceId: parseNullableString(record.oneOffSourceId, `${path}.oneOffSourceId`),
        defaultAmount: parseNonNegativeInteger(record.defaultAmount, `${path}.defaultAmount`),
        currentPayableAmount: parseNonNegativeInteger(record.currentPayableAmount, `${path}.currentPayableAmount`),
        currency: "JPY",
        amountSource: record.amountSource,
        collectionRoute: parseEnum(record.collectionRoute, STUDENT_BILLING_COLLECTION_ROUTES, `${path}.collectionRoute`),
        status: parseEnum(record.status, STUDENT_BILLING_UNIT_STATUSES, `${path}.status`),
        version: parsePositiveInteger(record.version, `${path}.version`),
        lockedAt: parseNullableString(record.lockedAt, `${path}.lockedAt`),
        lockedByPlatformUserId: parseNullableString(record.lockedByPlatformUserId, `${path}.lockedByPlatformUserId`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
function parseStudentSummary(value, path) {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["studentId", "displayName", "status", "regularSetting", "activePayerAssignment", "currentPayer", "draftBillingUnitCount", "lockedBillingUnitCount", "collectionPreference"], path);
    return {
        studentId: expectString(record.studentId, `${path}.studentId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        status: parseEnum(record.status, ["active", "inactive"], `${path}.status`),
        regularSetting: record.regularSetting === null ? null : parseRegularSetting(record.regularSetting, `${path}.regularSetting`),
        activePayerAssignment: record.activePayerAssignment === null ? null : parsePayerAssignment(record.activePayerAssignment, `${path}.activePayerAssignment`),
        currentPayer: record.currentPayer === null ? null : parseStudentBillingAdminPayer(record.currentPayer, `${path}.currentPayer`),
        draftBillingUnitCount: parseNonNegativeInteger(record.draftBillingUnitCount, `${path}.draftBillingUnitCount`),
        lockedBillingUnitCount: parseNonNegativeInteger(record.lockedBillingUnitCount, `${path}.lockedBillingUnitCount`),
        collectionPreference: record.collectionPreference === null ? null : parseStudentBillingAdminCollectionPreferenceDto(record.collectionPreference, `${path}.collectionPreference`)
    };
}
export function parseStudentBillingAdminOverview(value, path = "studentBillingAdminOverview") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["capabilities", "commercialAuthority", "students", "payers"], path);
    return {
        capabilities: parseStudentBillingAdminCapabilities(record.capabilities, `${path}.capabilities`),
        commercialAuthority: parseStudentBillingAdminCommercialAuthority(record.commercialAuthority, `${path}.commercialAuthority`),
        students: expectArray(record.students, parseStudentSummary, `${path}.students`),
        payers: expectArray(record.payers, parseStudentBillingAdminPayer, `${path}.payers`)
    };
}
export function parseStudentBillingAdminStudentDetail(value, path = "studentBillingAdminStudentDetail") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["capabilities", "commercialAuthority", "student", "regularSetting", "payerAssignments", "payers", "paidRegularEnrollments", "billingUnits", "collectionPreference"], path);
    const student = expectRecord(record.student, `${path}.student`);
    assertExactKeys(student, ["studentId", "displayName", "status"], `${path}.student`);
    return {
        capabilities: parseStudentBillingAdminCapabilities(record.capabilities, `${path}.capabilities`),
        commercialAuthority: parseStudentBillingAdminCommercialAuthority(record.commercialAuthority, `${path}.commercialAuthority`),
        student: {
            studentId: expectString(student.studentId, `${path}.student.studentId`),
            displayName: expectString(student.displayName, `${path}.student.displayName`),
            status: parseEnum(student.status, ["active", "inactive"], `${path}.student.status`)
        },
        regularSetting: record.regularSetting === null ? null : parseRegularSetting(record.regularSetting, `${path}.regularSetting`),
        payerAssignments: expectArray(record.payerAssignments, parsePayerAssignment, `${path}.payerAssignments`),
        payers: expectArray(record.payers, parseStudentBillingAdminPayer, `${path}.payers`),
        paidRegularEnrollments: expectArray(record.paidRegularEnrollments, (item, itemPath) => {
            const enrollment = expectRecord(item, itemPath);
            assertExactKeys(enrollment, ["enrollmentId", "lessonTitle", "status"], itemPath);
            return {
                enrollmentId: expectString(enrollment.enrollmentId, `${itemPath}.enrollmentId`),
                lessonTitle: expectString(enrollment.lessonTitle, `${itemPath}.lessonTitle`),
                status: parseEnum(enrollment.status, ["pendingStart", "active", "paused"], `${itemPath}.status`)
            };
        }, `${path}.paidRegularEnrollments`),
        billingUnits: expectArray(record.billingUnits, parseBillingUnit, `${path}.billingUnits`),
        collectionPreference: record.collectionPreference === null ? null : parseStudentBillingAdminCollectionPreferenceDto(record.collectionPreference, `${path}.collectionPreference`)
    };
}
function parseAdjustment(value, path) {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["adjustmentId", "actorPlatformUserId", "previousAmount", "newAmount", "originalDefaultAmount", "currency", "changedAt", "reason", "versionBefore", "versionAfter", "source"], path);
    if (record.currency !== "JPY" || record.source !== "manualOwnerAdjustment")
        throw new Error(`${path} contains invalid adjustment fields`);
    return {
        adjustmentId: expectString(record.adjustmentId, `${path}.adjustmentId`),
        actorPlatformUserId: parseStudentBillingIdentifier(record.actorPlatformUserId, `${path}.actorPlatformUserId`),
        previousAmount: parseNonNegativeInteger(record.previousAmount, `${path}.previousAmount`),
        newAmount: parseNonNegativeInteger(record.newAmount, `${path}.newAmount`),
        originalDefaultAmount: parseNonNegativeInteger(record.originalDefaultAmount, `${path}.originalDefaultAmount`),
        currency: "JPY",
        changedAt: expectString(record.changedAt, `${path}.changedAt`),
        reason: expectString(record.reason, `${path}.reason`),
        versionBefore: parsePositiveInteger(record.versionBefore, `${path}.versionBefore`),
        versionAfter: parsePositiveInteger(record.versionAfter, `${path}.versionAfter`),
        source: "manualOwnerAdjustment"
    };
}
export function parseStudentBillingAdminBillingUnitDetail(value, path = "studentBillingAdminBillingUnitDetail") {
    const record = expectRecord(value, path);
    assertExactKeys(record, ["capabilities", "billingUnit", "adjustments"], path);
    return {
        capabilities: parseStudentBillingAdminCapabilities(record.capabilities, `${path}.capabilities`),
        billingUnit: parseBillingUnit(record.billingUnit, `${path}.billingUnit`),
        adjustments: expectArray(record.adjustments, parseAdjustment, `${path}.adjustments`)
    };
}
